import { Button, Card, Checkbox, Col, Form, Input, Result, Row, Typography, Upload } from "antd";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { Sector, Case } from "@bindeleddet/core/types";
import { uploadButton } from "components/UploadButton/UploadButton";
import { getBase64 } from "helpers/functions";
import { IMAGE_ACCEPT, beforeUploadImage } from "helpers/fileValidation";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

const { Text } = Typography;

interface Props {
  newCase: boolean;
}

const CreateOrEditCase: React.FC<Props> = ({ newCase }) => {
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);
  const [previewFile, setPreviewFile] = useState<string | null>(null);
  const [removeFile, setRemoveFile] = useState<boolean>(false);
  const [image, setImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [existingCase, setExistingCase] = useState<Case>();

  // id is company-id if new case, case-id if editing
  const { id } = useParams<{ id: string }>();

  const history = useHistory();

  useEffect(() => {
    axios.get<Sector[]>("/companies/sectors/").then(response => {
      setSectors(response.data);
    });
  }, []);

  useEffect(() => {
    if (!id) return;
    if (!newCase) {
      setFetching(true);
      axios
        .get<Case>(`/website-contents/cases/${id}/`)
        .then(response => {
          setExistingCase(response.data);
        })
        .finally(() => setFetching(false));
    }
  }, [id, newCase]);

  useEffect(() => {
    if (existingCase) {
      form.setFieldValue("sectors", existingCase.sectors);
      form.setFieldValue("title", existingCase.title);
      form.setFieldValue("description", existingCase.description);
      setFile(null);
      setPreviewFile(null);
      setRemoveFile(false);
      setImage(null);
      setPreviewImage(null);
      setRemoveImage(false);
    }
  }, [existingCase, form]);

  const publishCase = (values: Case) => {
    if (!newCase) return;
    setLoading(true);
    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (key === "sectors" && Array.isArray(value)) {
        value.forEach(val => formData.append(key, String(val)));
      } else {
        formData.append(key, String(value));
      }
    });

    formData.append("updated_at", new Date().toISOString());
    formData.append("created_at", new Date().toISOString());
    formData.append("company", id);

    if (file) {
      formData.append("file", file);
    }
    if (image) {
      formData.append("image", image);
    }

    axios
      .post<Case>("/website-contents/cases/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        message("success", "Case publisert");
        history.push("/admin/resources/casebank");
      })
      .catch(() => message("error", "Kunne ikke publisere Case"))
      .finally(() => setLoading(false));
  };

  const updateCase = (values: Case) => {
    if (!existingCase) return;
    setLoading(true);

    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (key === "sectors" && Array.isArray(value)) {
        value.forEach(val => formData.append(key, String(val)));
      } else {
        formData.append(key, String(value));
      }
    });
    formData.append("updated_at", new Date().toISOString());
    if (file) {
      formData.append("file", file);
    }
    if (removeFile) {
      formData.append("file", "");
    }
    if (image) {
      formData.append("image", image);
    }
    if (removeImage) {
      formData.append("image", "");
    }
    axios
      .patch<Case>(`/website-contents/cases/${existingCase.id}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        message("success", "Case oppdatert");
        history.push("/admin/resources/casebank");
      })
      .catch(() => message("error", "Kunne ikke oppdatere Case"))
      .finally(() => setLoading(false));
  };

  useEffect(
    function setPreviewCaseFile() {
      if (file) {
        getBase64(file)
          .then((result: string | null) => {
            setPreviewFile(result);
          })
          .catch(() => {
            setPreviewFile(null);
          });
      }
    },
    [file]
  );

  useEffect(
    function setPreviewCaseImage() {
      if (image) {
        getBase64(image)
          .then((result: string | null) => {
            setPreviewImage(result);
          })
          .catch(() => {
            setPreviewImage(null);
          });
      }
    },
    [image]
  );

  const checkboxSectors = sectors.map(sector => {
    return (
      <Col key={sector.id} lg={4} md={6} sm={8} xs={12}>
        <Checkbox key={sector.id} value={sector.id}>
          {sector.name}
        </Checkbox>
      </Col>
    );
  });

  if (!fetching && !existingCase && !newCase)
    return (
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Result title="Fant ikke case" subTitle="Dobbeltsjekk URL eller prøv på nytt" status="404" />
      </div>
    );

  return (
    <>
      <Card title={newCase ? "Oppretter case" : "Oppdaterer case"} style={{ width: "100%" }}>
        <Form
          form={form}
          layout="vertical"
          requiredMark
          onFinish={newCase ? publishCase : updateCase}
          disabled={fetching}
        >
          <Form.Item
            label="Tittel"
            name="title"
            initialValue={existingCase?.title}
            rules={[{ required: true, message: "Legg til tittel" }]}
          >
            <Input placeholder="Skriv inn tittel..." style={{ marginBottom: "20px" }} />
          </Form.Item>
          <Form.Item
            label="Kort beskrivelse"
            name="description"
            initialValue={existingCase?.description}
            rules={[
              { required: true, message: "Legg til en kort beskrivelse av casen." },
              { max: 250, message: "Beskrivelsen kan ikke være lengre enn 250 tegn." },
            ]}
          >
            <Input.TextArea rows={5} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="sectors"
            label="Bransje"
            initialValue={existingCase?.sectors}
            rules={[{ required: true, message: "Vennligst velg minst en bransje casen er aktuell for" }]}
          >
            <Checkbox.Group>
              <Row style={{ minHeight: "66px" }}>{checkboxSectors}</Row>
            </Checkbox.Group>
          </Form.Item>
          <Row>
            <Col span={12}>
              <Form.Item label="Fil" required rules={[{ required: true, message: "Legg ved casen som en fil" }]}>
                <Upload
                  listType="picture-card"
                  className="avatar-uploader"
                  maxCount={2}
                  beforeUpload={f => {
                    setFile(f);
                    setRemoveFile(false);
                    // Return false to prevent immediate upload
                    return false;
                  }}
                  onRemove={() => {
                    setFile(null);
                    setPreviewFile(null);
                    setRemoveFile(true);
                  }}
                  fileList={
                    removeFile
                      ? []
                      : file
                      ? previewFile
                        ? [
                            {
                              uid: "-1",
                              name: file.name || "New case file",
                              url: previewFile,
                            },
                          ]
                        : []
                      : existingCase?.file
                      ? [
                          {
                            uid: "-1",
                            name: existingCase?.file?.substring(existingCase?.file?.lastIndexOf("/") + 1) || "Bilde",
                            status: "done",
                            url: existingCase?.file,
                          },
                        ]
                      : []
                  }
                >
                  {uploadButton}
                </Upload>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Bilde"
                tooltip="Her kan du laste opp et bilde som vises på forsiden av casen. Om du ikke laster opp et bilde vil bedriftens logo bli brukt."
                help={<Text style={{ color: "grey", fontSize: "12px" }}>Tillatte filtyper: JPG, PNG, GIF, WebP</Text>}
              >
                <Upload
                  listType="picture-card"
                  className="avatar-uploader"
                  maxCount={2}
                  accept={IMAGE_ACCEPT}
                  beforeUpload={f => {
                    const result = beforeUploadImage(f, "Bilde må være et bilde (JPG, PNG, GIF eller WebP)");
                    if (result === Upload.LIST_IGNORE) return result;
                    setImage(f);
                    setRemoveImage(false);
                    // Return false to prevent immediate upload
                    return false;
                  }}
                  onRemove={() => {
                    setImage(null);
                    setPreviewImage(null);
                    setRemoveImage(true);
                  }}
                  fileList={
                    removeImage
                      ? []
                      : image
                      ? previewImage
                        ? [
                            {
                              uid: "-1",
                              name: image.name || "New case image",
                              url: previewImage,
                            },
                          ]
                        : []
                      : existingCase?.image
                      ? [
                          {
                            uid: "-1",
                            name: existingCase?.image?.substring(existingCase?.image?.lastIndexOf("/") + 1) || "Bilde",
                            status: "done",
                            url: existingCase?.image,
                          },
                        ]
                      : []
                  }
                >
                  {uploadButton}
                </Upload>
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" loading={loading} style={{ width: "100%" }}>
            {newCase ? "Publiser case" : "Oppdater case"}
          </Button>
        </Form>
      </Card>
    </>
  );
};

export const CreateCase: React.FC = () => <CreateOrEditCase newCase />;
export const EditCase: React.FC = () => <CreateOrEditCase newCase={false} />;
